/**
 * Choices for a project's timezone, language and phone country, built from the
 * platform's own data (Intl) so no single country or continent comes first or
 * is preselected. Each list is alphabetical.
 */

export interface ChoiceOption {
  value: string;
  label: string;
}

// The TypeScript lib in this project predates these Intl APIs.
interface DisplayNamesLike { of(code: string): string | undefined }
type IntlWithExtras = typeof Intl & {
  supportedValuesOf?: (key: string) => string[];
  DisplayNames?: new (locales: string[], options: { type: string; languageDisplay?: string }) => DisplayNamesLike;
};
const intl = Intl as IntlWithExtras;

const sortByLabel = (a: ChoiceOption, b: ChoiceOption) => a.label.localeCompare(b.label);

/** IANA timezone names, e.g. Europe/London, plus UTC. */
export function timezoneOptions(): ChoiceOption[] {
  let names: string[] = [];
  try {
    names = intl.supportedValuesOf?.('timeZone') ?? [];
  } catch {
    names = [];
  }
  const set = new Set(names);
  set.add('UTC');
  return Array.from(set)
    .sort((a, b) => a.localeCompare(b))
    .map(value => ({ value, label: value }));
}

/** Codes that Intl names but that are not countries a phone number belongs to. */
const NOT_A_COUNTRY = new Set(['AC', 'CP', 'DG', 'EA', 'EU', 'EZ', 'IC', 'QO', 'TA', 'UN', 'XA', 'XB', 'ZZ']);

/** Two-letter country codes with their English names. */
export function phoneRegionOptions(): ChoiceOption[] {
  const names = intl.DisplayNames ? new intl.DisplayNames(['en'], { type: 'region' }) : null;
  const out: ChoiceOption[] = [];
  for (let a = 65; a <= 90; a += 1) {
    for (let b = 65; b <= 90; b += 1) {
      const code = String.fromCharCode(a, b);
      if (NOT_A_COUNTRY.has(code)) continue;
      let label: string | undefined;
      try { label = names?.of(code); } catch { label = undefined; }
      if (label && label !== code) out.push({ value: code, label });
    }
  }
  return out.sort(sortByLabel);
}

/** Language tags used to format numbers and dates. A broad, even-handed list. */
const LOCALE_TAGS = [
  'ar', 'bn-BD', 'cs-CZ', 'da-DK', 'de-DE', 'de-AT', 'de-CH', 'el-GR', 'en-AU', 'en-CA', 'en-GB', 'en-IE',
  'en-IN', 'en-NZ', 'en-US', 'en-ZA', 'es-AR', 'es-ES', 'es-MX', 'fi-FI', 'fr-BE', 'fr-CA', 'fr-FR', 'he-IL',
  'hi-IN', 'hu-HU', 'id-ID', 'it-IT', 'ja-JP', 'ko-KR', 'ms-MY', 'nb-NO', 'nl-BE', 'nl-NL', 'pl-PL',
  'pt-BR', 'pt-PT', 'ro-RO', 'ru-RU', 'sv-SE', 'sw-KE', 'th-TH', 'tr-TR', 'uk-UA', 'vi-VN', 'zh-CN', 'zh-TW',
];

export function localeOptions(): ChoiceOption[] {
  const names = intl.DisplayNames ? new intl.DisplayNames(['en'], { type: 'language', languageDisplay: 'dialect' }) : null;
  return LOCALE_TAGS
    .map(value => {
      let label: string | undefined;
      try { label = names?.of(value); } catch { label = undefined; }
      return { value, label: label && label !== value ? `${label} (${value})` : value };
    })
    .sort(sortByLabel);
}

/** The label to show for a stored value, falling back to the value itself (it may not be in the list). */
export function labelFor(options: ChoiceOption[], value: string): string {
  if (!value) return '';
  return options.find(o => o.value === value)?.label ?? value;
}
